import React, { useState, useEffect } from 'react'
import { Play, Square } from 'lucide-react'
import { toast } from 'sonner'
import { useQueryClient } from 'react-query'
import { cn } from '@/lib/utils'
import { useTimerStore } from '@/store/timerStore'
import { timeEntryService } from '@/services/timeEntryService'
import { refreshActiveTimer } from '@/lib/useActiveTimerSync'
import TimerStopDialog from './TimerStopDialog'

const formatElapsed = (startTime) => {
  const secs = Math.floor((Date.now() - new Date(startTime)) / 1000)
  const h = Math.floor(secs / 3600)
  const m = Math.floor((secs % 3600) / 60)
  const s = secs % 60
  if (h > 0) return `${h}:${String(m).padStart(2, '0')}`
  return `${m}:${String(s).padStart(2, '0')}`
}

const TaskTimerButton = ({ taskId, projectId, taskTitle }) => {
  const queryClient = useQueryClient()
  const { activeTimer, startTimer, stopTimer } = useTimerStore()
  const [loading, setLoading] = useState(false)
  const [stopOpen, setStopOpen] = useState(false)
  const [elapsed, setElapsed] = useState('')

  const isTrackingThis = activeTimer?.taskId === taskId
  const isTrackingOther = activeTimer && !isTrackingThis

  useEffect(() => {
    if (!isTrackingThis || !activeTimer?.startTime) return
    const interval = setInterval(() => setElapsed(formatElapsed(activeTimer.startTime)), 1000)
    setElapsed(formatElapsed(activeTimer.startTime))
    return () => clearInterval(interval)
  }, [isTrackingThis, activeTimer?.startTime])

  const handleStart = async (e) => {
    e.stopPropagation()
    setLoading(true)
    try {
      const res = await timeEntryService.start(taskId)
      const timer = res.data?.activeTimer
      if (timer) {
        startTimer({
          id: timer.id,
          taskId: timer.task_id,
          taskTitle: timer.task?.title || taskTitle,
          projectId: timer.task?.project_id || projectId,
          projectName: timer.task?.project?.name || '',
          startTime: timer.start_time,
        })
      }
      queryClient.invalidateQueries(['projects', String(projectId), 'tasks'])
      toast.success('Timer started')
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to start timer')
    } finally {
      setLoading(false)
    }
  }

  const handleStopConfirm = async (note) => {
    setLoading(true)
    try {
      await timeEntryService.stop(activeTimer.id, note)
      stopTimer()
      queryClient.invalidateQueries(['projects', String(projectId), 'tasks'])
      queryClient.invalidateQueries('time-summary')
      toast.success('Timer stopped')
      setStopOpen(false)
    } catch (err) {
      if (err.response?.status === 404) {
        // Already stopped elsewhere (another tab, or an AI agent via MCP)
        refreshActiveTimer(queryClient)
        setStopOpen(false)
        toast.info('That timer was already stopped elsewhere')
        return
      }
      toast.error(err.response?.data?.error || 'Failed to stop timer')
    } finally {
      setLoading(false)
    }
  }

  if (isTrackingThis) {
    return (
      <>
        <button
          onClick={(e) => { e.stopPropagation(); setStopOpen(true) }}
          className="inline-flex items-center gap-1 text-xs text-primary shrink-0 hover:text-primary/80 transition-colors"
        >
          <Square className="h-3 w-3 fill-current animate-pulse" />
          <span className="font-mono tabular-nums">{elapsed}</span>
        </button>
        <TimerStopDialog
          open={stopOpen}
          onOpenChange={setStopOpen}
          taskTitle={taskTitle}
          elapsedLabel={elapsed}
          onConfirm={handleStopConfirm}
          loading={loading}
        />
      </>
    )
  }

  return (
    <button
      onClick={handleStart}
      disabled={loading}
      className={cn(
        'inline-flex items-center justify-center h-6 w-6 rounded hover:bg-accent text-muted-foreground hover:text-foreground transition-colors shrink-0',
        loading && 'opacity-50'
      )}
      title="Start timer"
    >
      <Play className="h-3 w-3" />
    </button>
  )
}

export default TaskTimerButton
